// @ts-nocheck
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpcClient } from "@/lib/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatMoney } from "@/lib/utils";
import { DollarSign, Loader2, CheckCircle2 } from "lucide-react";

export interface QuickReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: {
    type: "ORDER" | "DELIVERY";
    id: string;
    code?: string;
    customerName?: string;
    defaultAmount?: number;
  } | null;
  onSuccess?: () => void;
}

export function QuickReceiptDialog({
  open,
  onOpenChange,
  source,
  onSuccess,
}: QuickReceiptDialogProps) {
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("CHUYEN_KHOAN");
  const [referenceNo, setReferenceNo] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  // Số phải thu / đã thu / còn lại lấy từ máy chủ: mở phiếu thu đúng số còn lại, không thu trùng
  const { data: collection, isFetching: loadingCollection } = useQuery({
    queryKey: ["receipt-collection-status", source?.type, source?.id],
    queryFn: () => trpcClient.receipts.collectionStatus.query({ type: source!.type, id: source!.id }),
    enabled: Boolean(open && source?.id),
    staleTime: 0,
  });
  const remaining = collection ? Math.max(0, Number(collection.remaining || 0)) : null;
  const fullyPaid = collection != null && Number(collection.due || 0) > 0 && (remaining ?? 0) <= 0;

  useEffect(() => {
    if (open && remaining != null) setAmount(remaining);
  }, [open, remaining]);

  useEffect(() => {
    if (source && open) {
      const initialAmount = Math.max(0, Number(source.defaultAmount || 0));
      setAmount(remaining ?? initialAmount);
      setReferenceNo(source.code ? `TT-${source.code}` : "");
      setNotes(
        source.type === "ORDER"
          ? `Thu tiền thanh toán đơn hàng ${source.code || source.id}`
          : `Thu tiền phiếu giao hàng ${source.code || source.id}`
      );
      setPaymentMethod("CHUYEN_KHOAN");
    }
  }, [source, open]);

  const receiptFromOrderMutation = useMutation({
    mutationFn: async () => {
      if (!source) throw new Error("Không có thông tin nguồn thu");
      return await trpcClient.receipts.createReceiptFromOrder.mutate({
        orderId: source.id,
        amount,
        paymentMethod,
        referenceNo: referenceNo || undefined,
        notes: notes || undefined,
      });
    },
    onSuccess: (res: any) => {
      toast.success(
        `Đã lập phiếu thu ${res.receiptCode} (${formatMoney(res.amount)} đ) thành công!`
      );
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["order-receipts-paginated"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
      queryClient.invalidateQueries({ queryKey: ["order-settlement"] });
      queryClient.invalidateQueries({ queryKey: ["receipt-collection-status"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi tạo phiếu thu");
    },
  });

  const receiptFromDeliveryMutation = useMutation({
    mutationFn: async () => {
      if (!source) throw new Error("Không có thông tin nguồn thu");
      return await trpcClient.receipts.createReceiptFromDelivery.mutate({
        deliveryId: source.id,
        amount,
        paymentMethod,
        referenceNo: referenceNo || undefined,
        notes: notes || undefined,
      });
    },
    onSuccess: (res: any) => {
      toast.success(
        `Đã lập phiếu thu ${res.receiptCode} (${formatMoney(res.amount)} đ) thành công!`
      );
      queryClient.invalidateQueries({ queryKey: ["delivery"] });
      queryClient.invalidateQueries({ queryKey: ["deliveries"] });
      queryClient.invalidateQueries({ queryKey: ["order"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
      queryClient.invalidateQueries({ queryKey: ["order-settlement"] });
      queryClient.invalidateQueries({ queryKey: ["receipt-collection-status"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi khi tạo phiếu thu");
    },
  });

  const isPending =
    receiptFromOrderMutation.isPending || receiptFromDeliveryMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || amount <= 0) {
      toast.error("Vui lòng nhập số tiền thu hợp lệ (> 0)");
      return;
    }

    if (source?.type === "ORDER") {
      receiptFromOrderMutation.mutate();
    } else if (source?.type === "DELIVERY") {
      receiptFromDeliveryMutation.mutate();
    }
  };

  if (!source) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700">
                <DollarSign className="w-4 h-4" />
              </span>
              Lập Phiếu Thu Nhanh (1-Chạm)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {source.type === "ORDER" ? "Đơn hàng" : "Phiếu giao hàng"}:{" "}
              <span className="font-semibold text-slate-700">
                {source.code || source.id}
              </span>
              {source.customerName && ` • Khách hàng: ${source.customerName}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 text-xs">
            {collection && Number(collection.due || 0) > 0 && (
              <div
                data-testid="receipt-collection-status"
                className={`rounded-lg border px-3 py-2 ${fullyPaid ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}
              >
                {fullyPaid
                  ? `Đã thu đủ ${formatMoney(collection.due)} đ — không cần lập thêm phiếu thu.`
                  : `Phải thu ${formatMoney(collection.due)} đ · Đã thu ${formatMoney(collection.collected)} đ · Còn lại ${formatMoney(remaining)} đ`}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">
                Số tiền thu (VND) <span className="text-rose-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={amount || ""}
                  onChange={(e) => setAmount(Number(e.target.value) || 0)}
                  placeholder="Nhập số tiền..."
                  className="font-mono text-sm font-semibold pr-16"
                  required
                />
                <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-medium pointer-events-none">
                  VND
                </span>
              </div>
              <p className="text-[11px] text-emerald-600 font-medium">
                {formatMoney(amount)} đ
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">
                Phương thức thanh toán
              </Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="text-xs">
                  <SelectValue placeholder="Chọn phương thức..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHUYEN_KHOAN">Chuyển khoản (VietQR / Ngân hàng)</SelectItem>
                  <SelectItem value="TIEN_MAT">Tiền mặt</SelectItem>
                  <SelectItem value="CAN_TRU">Cấn trừ công nợ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">
                Số chứng từ / Mã tham chiếu
              </Label>
              <Input
                type="text"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                placeholder="VD: FT260906888..."
                className="text-xs font-mono"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">
                Ghi chú phiếu thu
              </Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Nội dung diễn giải thu tiền..."
                className="text-xs resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={() => onOpenChange(false)}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isPending || loadingCollection || fullyPaid || amount <= 0 || (remaining != null && amount > remaining + 1)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Đang ghi nhận...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  Xác Nhận Thu {formatMoney(amount)} đ
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
